import React, { useState, useEffect, useRef } from 'react'

import {
    PharmacySaveLabelSettings,
    PharmacySyncLabelSettings
} from '../../../services/reducers/pharmacy/other/commonReducerForInitalData'
import { useCustomDispatch } from '../../../hooks'
import { ProfilePicLoader } from '../../../components/common/loadingView'
import { useCommonReducerSelector } from '../../../services/selectors/commonReducerSelector'
import { ModalCloseIcon } from '../../../assets/svg/icons'
import { Printer } from 'lucide-react'
import label1 from '../../../assets/images/label_1.jpg'
import label2 from '../../../assets/images/label_2.jpg'

const toInput = (value) => (value === undefined || value === null ? '' : String(value))

// One labelled number box (no button - the popup has a single Update).
const NumberField = ({ id, label, placeholder, value, error, onChange, className = '' }) => (
    <div className={className}>
        <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
        <input
            type="text"
            inputMode="decimal"
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full border ${error ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100`}
        />
        {error && <div className="mt-1 text-xs text-red-500">{error}</div>}
    </div>
)

const SectionTitle = ({ children }) => (
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{children}</h3>
)

// The "Label Setting" popup. Opened from the profile menu (LabelSetting below)
// and from the "Print Label Setting" button on Dispense -> Delivery Labels.
// Everything is edited locally and saved together by the one Update button at
// the bottom; closing without Update saves nothing.
export const LabelSettingModal = ({ showModal, onClose }) => {

    const {
        updateLabelToMarginResponse,
        updateLabelPrintHeaderResponse,
        updateLabelHeaderTopMarginResponse,
        updateLabelPrintTypeResponse,
        updateLabelPrintLogoResponse,
        updateLabelLeftMarginResponse
    } = useCommonReducerSelector()

    const dispatch = useCustomDispatch()

    const [form, setForm] = useState(null)
    const editedRef = useRef(false)
    const [errors, setErrors] = useState({})
    const [saving, setSaving] = useState(false)

    // On open: show what the store has straight away (loaded at login / last
    // save), then refresh from the server - the store is empty after a page
    // refresh - unless the user has already started editing.
    useEffect(() => {
        if (!showModal) {
            setForm(null)
            editedRef.current = false
            setErrors({})
            return
        }

        let cancelled = false

        setForm({
            topMargin: toInput(updateLabelToMarginResponse?.data),
            leftMargin: toInput(updateLabelLeftMarginResponse?.data ?? 0),
            printHeader: !!updateLabelPrintHeaderResponse?.data,
            printLogo: !!updateLabelPrintLogoResponse?.data,
            headerTopMargin: toInput(updateLabelHeaderTopMarginResponse?.data ?? 0),
            upsideDown: updateLabelPrintTypeResponse?.data === 'toptodown',
        })

        dispatch(PharmacySyncLabelSettings()).then((settings) => {
            if (cancelled || !settings || editedRef.current) return
            setForm((current) => ({
                topMargin: settings.label_top_margin !== undefined && settings.label_top_margin !== ''
                    ? toInput(settings.label_top_margin)
                    : current?.topMargin ?? '',
                leftMargin: toInput(settings.label_left_margin ?? 0),
                printHeader: !!settings.label_print_header,
                printLogo: !!settings.label_print_logo,
                headerTopMargin: toInput(settings.label_header_top_margin ?? 0),
                upsideDown: settings.label_print_type === 'toptodown',
            }))
        })

        return () => { cancelled = true }
        // Only re-run when the popup opens or closes; the store values above are
        // a snapshot for the first paint.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, dispatch])

    const setField = (field, value) => {
        editedRef.current = true
        setForm((current) => ({ ...current, [field]: value }))
        setErrors((current) => ({ ...current, [field]: '' }))
    }

    const validate = () => {
        const next = {}
        const { topMargin, leftMargin, headerTopMargin, printHeader } = form

        if (topMargin === '') {
            next.topMargin = 'Top margin is required'
        } else if (isNaN(topMargin) || Number(topMargin) <= 0) {
            next.topMargin = 'Please enter a valid positive number'
        }

        if (leftMargin !== '' && (isNaN(leftMargin) || Number(leftMargin) < -26)) {
            next.leftMargin = 'Please enter a number of -26 or more'
        }

        // 0 is valid here - it pins the header to the very top of the label.
        if (printHeader && headerTopMargin !== '' && (isNaN(headerTopMargin) || Number(headerTopMargin) < 0)) {
            next.headerTopMargin = 'Please enter 0 or a positive number'
        }

        setErrors(next)
        return Object.keys(next).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (saving || !form || !validate()) return

        const payload = {
            label_top_margin: form.topMargin,
            label_left_margin: Number(form.leftMargin || 0),
            // "Space after SIG" is hidden for now (column kept for later use), so
            // save 0 - an old value must not keep adding a gap nobody can see.
            label_sig_gap: 0,
            label_print_header: form.printHeader ? 1 : 0,
            label_print_logo: form.printLogo ? 1 : 0,
            label_print_type: form.upsideDown ? 'toptodown' : 'regular',
        }

        // Header margin is only shown (and so only checked) while the header is on.
        if (form.printHeader) {
            payload.label_header_top_margin = Number(form.headerTopMargin || 0)
        }

        setSaving(true)
        const saved = await dispatch(PharmacySaveLabelSettings(payload))
        setSaving(false)

        if (saved) {
            onClose()
        }
    }

    if (!showModal || !form) return null

    const directions = [
        { upsideDown: false, img: label1, title: 'Normal' },
        { upsideDown: true, img: label2, title: 'Upside down' }
    ]

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <form onSubmit={handleSubmit} className="flex w-[560px] max-w-full max-h-full flex-col overflow-hidden rounded-xl bg-white shadow-xl">
                {/* Title bar - stays put while the body scrolls. */}
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-50 text-blue-700">
                            <Printer size={18} />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold leading-tight text-gray-800">Label Setting</h2>
                            <p className="text-xs text-gray-500">Applies to every label this pharmacy prints.</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close label settings"
                        className="bg-tableHeader text-white rounded-full p-1 w-6 h-6 flex items-center justify-center hover:opacity-90"
                    >
                        <ModalCloseIcon />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4">
                    <SectionTitle>Label position</SectionTitle>
                    <div className="grid grid-cols-2 gap-3">
                        <NumberField
                            id="topMargin"
                            label="Top margin"
                            placeholder="e.g. 40"
                            value={form.topMargin}
                            error={errors.topMargin}
                            onChange={(value) => setField('topMargin', value)}
                        />

                        {/* Moves the whole label right (+) or left (-). 0 = where it prints today. */}
                        <NumberField
                            id="leftMargin"
                            label="Left margin"
                            placeholder="0"
                            value={form.leftMargin}
                            error={errors.leftMargin}
                            onChange={(value) => setField('leftMargin', value)}
                        />
                    </div>

                    <div className="my-3 border-t border-gray-100"></div>

                    <SectionTitle>Pharmacy header</SectionTitle>
                    <div className={`rounded-lg border p-3 transition-colors ${form.printHeader ? 'border-blue-100 bg-blue-50/40' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex items-center gap-3">
                            <label className="inline-flex shrink-0 cursor-pointer items-center" title="Print pharmacy header on labels">
                                <input type="checkbox" className="sr-only peer"
                                    checked={form.printHeader}
                                    onChange={() => setField('printHeader', !form.printHeader)} />
                                <div className="relative h-7 w-12 rounded-full bg-gray-300 transition-colors peer-focus:ring-2 peer-focus:ring-blue-200 peer-checked:bg-blue-600 after:absolute after:left-1 after:top-1 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:transition-transform peer-checked:after:translate-x-5"></div>
                            </label>
                            <div>
                                <p className="text-sm font-semibold leading-tight text-gray-800">Print pharmacy header</p>
                                <p className="text-xs leading-tight text-gray-500">Name, address, phone and fax on every label.</p>
                            </div>

                            {/* Logo only prints inside the header, so it sits beside that switch. */}
                            {form.printHeader && (
                                <label className="ml-auto flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5" title="Print pharmacy logo in the header">
                                    <input type="checkbox"
                                        className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-200"
                                        checked={form.printLogo}
                                        onChange={() => setField('printLogo', !form.printLogo)} />
                                    <span className="text-sm font-medium text-gray-800">Logo</span>
                                </label>
                            )}
                        </div>

                        {form.printHeader && (
                            <NumberField
                                className="mt-3"
                                id="headerTopMargin"
                                label="Header top margin"
                                placeholder="0"
                                value={form.headerTopMargin}
                                error={errors.headerTopMargin}
                                onChange={(value) => setField('headerTopMargin', value)}
                            />
                        )}
                    </div>

                    <div className="my-3 border-t border-gray-100"></div>

                    <SectionTitle>Print direction</SectionTitle>
                    <div className="grid grid-cols-2 gap-3">
                        {directions.map(({ upsideDown, img, title }) => {
                            const selected = form.upsideDown === upsideDown
                            return (
                                <label key={title}
                                    className={`flex cursor-pointer flex-col items-center gap-1.5 rounded-lg border-2 p-2 transition-colors ${selected ? 'border-blue-600 bg-blue-50/40' : 'border-gray-200 hover:border-gray-300'}`}>
                                    <img src={img} alt={title} className="w-full max-w-[120px] rounded" />
                                    <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
                                        <input type="radio"
                                            name="label_print_type"
                                            className="h-4 w-4 cursor-pointer text-blue-600"
                                            checked={selected}
                                            onChange={() => setField('upsideDown', upsideDown)} />
                                        {title}
                                    </span>
                                </label>
                            )
                        })}
                    </div>
                </div>

                {/* Footer - always visible. */}
                <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-5 py-2.5">
                    <button
                        type="button"
                        onClick={onClose}
                        className="min-w-24 rounded-md border border-gray-300 bg-white py-1.5 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="min-w-24 bg-blue-700 text-white rounded-md py-1.5 px-4 text-sm font-medium flex justify-center items-center hover:bg-blue-800 disabled:opacity-60"
                    >
                        {saving && <ProfilePicLoader color='white' />}
                        <span className='ml-2'>Update</span>
                    </button>
                </div>
            </form>
        </div>
    )
}

const LabelSetting = () => {
    const [showModal, setShowModal] = useState(false)

    return (
        <>
            <li onClick={() => setShowModal(true)}
                className="btnnn"
            >
                <a className="flex gap-4 items-center px-5 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white grey-color rubik-400 fs-16">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M3 11.5V7a2 2 0 012-2h4.5a2 2 0 011.414.586l7.5 7.5a2 2 0 010 2.828l-5.5 5.5a2 2 0 01-2.828 0l-7.5-7.5A2 2 0 013 11.5z" />
                    </svg>Label Setting
                </a>
            </li>

            <LabelSettingModal showModal={showModal} onClose={() => setShowModal(false)} />
        </>
    )
}

export default LabelSetting
